import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  PlusOutlined,
  DeleteOutlined,
  SettingOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";
import { Skeleton, Space, Button, Tooltip, Dropdown } from "antd";
import { useDispatch, useSelector } from "react-redux";
// import { formatCurrency } from "../../assets/helpers";
import { ReactComponent as EditIcon } from "../../assets/Icons/Editicon.svg";
import { fetchWhatsappCampaigns } from "../../store/whatsappCampaignSlice";
// import { fetchUserFilters } from "../../store/filters";
// import { fetchTemplates } from "../../store/templateSlice";
import { AddWhatsappCampaign, EditWhatsappCampaign, deleteWhatsappCampaign, changeWhatsappCampaignStatus } from "../../assets/api/campaignService";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import WhatsappCampaignModal from "../../components/CampaignComp/WhatsappCampaignModal/WhatsappCampaignModal";
import moment from "moment";
import "./WhatsappCampaignPage.scss";
import DeleteCampaignModal from "../../components/CampaignComp/DeleteCampaignModal/DeleteCampaignModal";

const EMPTY = "—";

const StaticSkeleton = () => <Skeleton.Input size="small" active />;

const TextOrDash = ({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (text === null || text === undefined || text === "") {
    return <span className="waCampaignEmpty">{EMPTY}</span>;
  }
  return <span className="waCampaignText">{text}</span>;
};

// Memoized cell components to prevent unnecessary re-renders
const MemoizedTextCell = memo(({ text, loading }) => (
  <TextOrDash text={text} loading={loading} />
));
MemoizedTextCell.displayName = "MemoizedTextCell";

const NameCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="waCampaignEmpty">{EMPTY}</span>;
  return <span className="waCampaignName">{text}</span>;
});
NameCell.displayName = "NameCell";

/* Figures are set in tabular numerals so a column of them lines up. */
const NumberCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (text === null || text === undefined || text === "") {
    return <span className="waCampaignEmpty">{EMPTY}</span>;
  }
  return <span className="waCampaignNumber">{text}</span>;
});
NumberCell.displayName = "NumberCell";

/* The template is an identifier the campaign points at, so it is set apart
   from the prose cells rather than reading as another word in the row. */
const TemplateCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="waCampaignEmpty">{EMPTY}</span>;
  return <span className="waCampaignTemplate">{text}</span>;
});
TemplateCell.displayName = "TemplateCell";

const CreatedAtCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="waCampaignEmpty">{EMPTY}</span>;
  const when = moment(text);
  if (!when.isValid()) return <span className="waCampaignEmpty">{EMPTY}</span>;
  return (
    <span className="waCampaignDate">
      <span className="waCampaignDate__day">{when.format("DD MMM YYYY")}</span>
      <span className="waCampaignDate__time">{when.format("hh:mm A")}</span>
    </span>
  );
});
CreatedAtCell.displayName = "CreatedAtCell";

/* A campaign moves through start / pause / stop / resume, so the chip is
   banded by what the status means rather than being one flat colour. */
const statusBand = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "active":
    case "running":
    case "started":
      return "is-active";
    case "paused":
      return "is-paused";
    case "stopped":
    case "inactive":
      return "is-stopped";
    case "completed":
    case "finished":
      return "is-done";
    default:
      return "is-draft";
  }
};

const StatusCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="waCampaignEmpty">{EMPTY}</span>;
  return (
    <span className={`waCampaignStatus ${statusBand(text)}`}>
      <i />
      {text.charAt(0).toUpperCase() + text.slice(1)}
    </span>
  );
});
StatusCell.displayName = "StatusCell";


const WhatsappCampaignPage = () => {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.auth);
  const { whatsapp_campaigns_list, whatsapp_campaign_list_loading } = useSelector((state) => state.whatsappCampaign);
  // const { filter_list } = useSelector((state) => state?.filters);
  // const { templates_list } = useSelector((state) => state.template);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCampaignData, setSelectedCampaignData] = useState(null);
  const [deleteCampaignId, setDeleteCampaignId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadSubmit, setLoadSubmit] = useState(false);


  // useEffect(() => {
  //   if (!user?.is_tc) {
  //     dispatch(fetchUserFilters());
  //     dispatch(fetchTemplates()); // Fetching large page size for dropdown options
  //   }
  // }, [dispatch, user?.is_tc]);

  // const portfoliosData = filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
  // const dynamicPortfoliosOptions = portfoliosData.map((d) => ({
  //   value: d.id || d,
  //   label: d.name || d.key || d,
  // }));

  // const templateResults = templates_list?.data?.results || templates_list?.results || [];
  // const messageTemplateOptions = templateResults.map((t) => ({
  //   value: t.id,
  //   label: t.name,
  // }));

  const handleEditClick = useCallback((record) => {
    console.log("Edit clicked for record:", record);
    setIsEdit(true);
    setSelectedCampaignData(record);
    setIsModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((record) => {
    console.log("Delete clicked for:", record);    
    setDeleteCampaignId(record.id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleStatusChange = useCallback(async (id, status) => {
    const payload = {
      action: status
    };
    try {
      await changeWhatsappCampaignStatus(id, payload, token);
      dispatch(fetchWhatsappCampaigns({ page: currentPage, page_size: pageSize }));
      CustomNotification.show("Whatsapp Campaign status changed successfully", "success");
    } catch (err) {
      console.error("Error changing campaign status:", err);
      CustomNotification.show("Error changing whatsapp campaign status", "error");
    }
  }, [currentPage, pageSize, token, dispatch]);

  const getStatusMenuItems = useCallback((recordId) => [
    {
      key: "start",
      label: "Start",
      onClick: () => handleStatusChange(recordId, "start"),
    },
    {
      key: "pause",
      label: "Pause",
      onClick: () => handleStatusChange(recordId, "pause"),
    },
    {
      key: "stop",
      label: "Stop",
      onClick: () => handleStatusChange(recordId, "stop"),
    },
  ], [handleStatusChange]);


  useEffect(() => {
    dispatch(fetchWhatsappCampaigns({ page: currentPage, page_size: pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handleSaveCampaign = useCallback(async (payload) => {
    setLoadSubmit(true);
    try {
      if (isEdit && selectedCampaignData) {
        await EditWhatsappCampaign(payload, selectedCampaignData.id, token);
        CustomNotification.show("Whatsapp Campaign updated successfully", "success");
      } else {
        await AddWhatsappCampaign(payload, token);
        CustomNotification.show("Whatsapp Campaign added successfully", "success");
      }
      setIsModalOpen(false);
      setSelectedCampaignData(null);
      setIsEdit(false);
      dispatch(fetchWhatsappCampaigns({ page: currentPage, page_size: pageSize }));
    } catch (err) {
      console.error("Error saving campaign:", err);
      CustomNotification.show(err?.message || "Error saving whatsapp campaign", "error");
    } finally {
      setLoadSubmit(false);
    }
  }, [isEdit, selectedCampaignData, token, currentPage, pageSize, dispatch]);

  const handleDeleteCampaign = useCallback(async () => {
    setLoadSubmit(true);

    try {
      const res = await deleteWhatsappCampaign(deleteCampaignId, token);
      console.log(res, "====campaign delete response====")

      if (res?.status === 200 || res?.status === 201) {
        CustomNotification.show("Campaign deleted successfully", "success");
        setLoadSubmit(false);
        setIsDeleteModalOpen(false);
        setDeleteCampaignId(null);
        dispatch(fetchWhatsappCampaigns({ page: currentPage, page_size: pageSize }));
      }
    } catch (err) {
      CustomNotification.show(err?.message || "Error deleting campaign", "error");
      setLoadSubmit(false);
      setIsDeleteModalOpen(false);
      setDeleteCampaignId(null);
    }
  }, [deleteCampaignId, currentPage, pageSize, token, dispatch]);

  const loading = whatsapp_campaign_list_loading;
  const listData = whatsapp_campaigns_list?.data;
  console.log(listData, "====listData====")

  const data = useMemo(()=> Array.isArray(listData)
    ? listData
    : listData && Array.isArray(listData.results)
      ? listData.results
      : [], [listData]);

  const columns = useMemo(() => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) =>
        <NameCell text={text} loading={loading} />,
    },
    {
      title: "Portfolio",
      dataIndex: ["portfolio", "name"],
      key: "portfolio",
      render: (text) =>
        <MemoizedTextCell text={text} loading={loading} />,
    },
    {
      title: "Message Template",
      dataIndex: ["message_template", "name"],
      key: "message_template",
      render: (text) =>
        <TemplateCell text={text} loading={loading} />,
    },
    {
      title: "Leads Count",
      dataIndex: "leads_count",
      key: "leads_count",
      render: (text) =>
        <NumberCell text={text} loading={loading} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) =>
        <StatusCell text={text} loading={loading} />,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) =>
        <CreatedAtCell text={text} loading={loading} />
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: user?.is_tc ? 80 : 140,
      render: (_, record) => {
        if (loading) return <StaticSkeleton />

        const isDisabled = user?.is_tc && record?.type?.toLowerCase() === "predictive"

        return (
          <Space size={8}>
            <Tooltip title="Campaign Control">
              <Dropdown
                menu={{ items: getStatusMenuItems(record.id) }}
                trigger={["click"]}
                disabled={isDisabled}
              >
                <Button
                  className="waCampaignIconBtn is-control"
                  icon={<SettingOutlined />}
                  disabled={isDisabled}
                />
              </Dropdown>
            </Tooltip>
            {!user?.is_tc && (
              <>
                <Tooltip title="Edit campaign">
                  <Button
                    className="waCampaignIconBtn is-edit"
                    icon={<EditIcon />}
                    onClick={() => handleEditClick(record)}
                  />
                </Tooltip>
                <Tooltip title="Delete campaign">
                  <Button
                    className="waCampaignIconBtn is-delete"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteClick(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        );
      }
    },
  ], [loading, handleStatusChange, handleEditClick, handleDeleteClick, user?.is_tc]);

  const skeletonData = useMemo(() => 
    Array(5).fill({}).map((_, i) => ({ id: `skeleton-${i}` })),
    []
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedCampaignData(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteCampaignId(null);
  }, []);

  const handleAddCampaign = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleTableChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const totalItems = useMemo(() => 
    whatsapp_campaigns_list?.data?.count || data?.length || 0,
    [whatsapp_campaigns_list, data]
  );


  return (
    <div className="whatsappCampaignPageMainWrapper">
      {isModalOpen && <WhatsappCampaignModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveCampaign}
        // portfolioOptions={dynamicPortfoliosOptions}
        load={loadSubmit}
        isEdit={isEdit}
        initialData={selectedCampaignData}
      />}

      {isDeleteModalOpen && <DeleteCampaignModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onDelete={handleDeleteCampaign}
        /* was `load`, which the dialog does not read - the Delete button
           never showed its pending state */
        loading={loadSubmit}
      />}

      {/* Flush body + an inner `*-report-table` wrapper is how the dashboard
          panels carry a table, so the rows sit at the same inset here. */}
      <PanelCard
        title="All Whatsapp Campaigns"
        icon={<WhatsAppOutlined />}
        accent="brand"
        padded={false}
        actions={
          <Button
            className="addWhatsappCampaignBtn"
            icon={<PlusOutlined />}
            onClick={handleAddCampaign}
          >
            Add Campaign
          </Button>
        }
      >
        <div className="whatsapp-report-table">
          <ReusableTable
            columns={columns}
            dataSource={loading ? skeletonData : data}
            totalItems={totalItems}
            rowKey="id"
            CurrentPageNo={currentPage}
            pageSize={pageSize}
            triggerChange={handleTableChange}
          />
        </div>
      </PanelCard>
    </div>
  );
};

export default memo(WhatsappCampaignPage);